import React from 'react';

type ButtonVariant =
  | 'primary'
  | 'outline-primary'
  | 'outline-danger'
  | 'outline-grey'
  | 'red'
  | 'blue'
  | 'outline-red'
  | 'outline-blue'
  | 'gray-bg';

interface ButtonProps {
  children: React.ReactNode;
  variant?: ButtonVariant;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  className?: string;
  borderColor?: string;
}

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  icon,
  iconPosition = 'right',
  onClick,
  type = 'button',
  disabled = false,
  loading = false,
  fullWidth = false,
  className = '',
  borderColor,
}) => {
  const baseStyles = `
    inline-flex items-center justify-center
    min-w-[96px] min-h-[44px] h-[44px]
    px-4 py-[10px]
    rounded-lg
    border border-solid
    font-sans font-bold text-[16px] leading-[100%] tracking-[0.02em]
    text-center
    gap-1
    transition-all duration-200
    cursor-pointer
    disabled:opacity-50 disabled:cursor-not-allowed
  `;

  const variantStyles: Record<ButtonVariant, string> = {
    primary: `
      bg-[#00428C] border-[#00428C] text-white
      hover:bg-[#003470] hover:border-[#003470]
      active:bg-[#002855]
    `,
    'outline-primary': `
      bg-[#FFF] border-[#00428C] text-black
      hover:bg-[#00428C]/10
      active:bg-[#00428C]/20
    `,
    'outline-danger': `
      bg-[#FFF] border-[#DC2626] text-[#DC2626]
      hover:bg-[#DC2626]/10
      active:bg-[#DC2626]/20
    `,
    'outline-grey': `
      bg-[#FFF] border-[#B3B3B3] text-[#1A1A1A]
      hover:bg-[#B3B3B3]/10
      active:bg-[#B3B3B3]/20
    `,
    red: `
      bg-[#EC0000] border-[#EC0000] text-white
      hover:bg-[#CC0000] hover:border-[#CC0000]
      active:bg-[#AA0000]
    `,
    blue: `
      bg-[#0055AD] border-[#0055AD] text-white
      hover:bg-[#003D7A] hover:border-[#003D7A]
      active:bg-[#002E5C]
    `,
    'outline-red': `
      bg-[#FFF] border-[#EC0000] text-[#EC0000]
      hover:bg-[#EC0000]/10
      active:bg-[#EC0000]/20
    `,
    'outline-blue': `
      bg-[#FFF] border-[#0055AD] text-[#0055AD]
      hover:bg-[#0055AD]/10
      active:bg-[#0055AD]/20
    `,
    'gray-bg': `
      bg-[#FFF] border border-[#B3B3B3] text-black
      hover:bg-[#E5E5E5]
      active:bg-[#D4D4D4]
    `,
  };

  const widthStyles = fullWidth ? 'w-full' : 'w-[128px]';

  const customBorder = borderColor ? `border border-[${borderColor}]` : '';

  const combinedStyles = `
    ${baseStyles}
    ${variantStyles[variant]}
    ${widthStyles}
    ${customBorder}
    ${className}
  `
    .replace(/\s+/g, ' ')
    .trim();

  return (
    <button type={type} onClick={onClick} disabled={disabled || loading} className={combinedStyles}>
      {loading ? (
        <svg
          className="animate-spin h-5 w-5"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      ) : (
        <>
          {icon && iconPosition === 'left' && (
            <span className="flex items-center mr-2">{icon}</span>
          )}
          <span>{children}</span>
          {icon && iconPosition === 'right' && (
            <span className="flex items-center ml-2">{icon}</span>
          )}
        </>
      )}
    </button>
  );
};

export default Button;
